/* Orrery — the chart's facts as words. One computation, used by the page
   (assets/orrery.js composes the readings from it) and by the test harness
   (tests/merge-fields.mjs prints it for the reference charts).

   The rule it exists for: nothing the chart determines may be written as
   literal text anywhere. The library (content/readings.json) holds voice; the
   engine holds fact; they meet only here, at render, through the merge fields
   returned by factsFor(). Loaded after orrery-engine.js; no modules. */
(function () {
  var ENGINE = window.OrreryEngine;
  var SIGN_NAMES = ENGINE.SIGN_NAMES;
  var ELEMENTS = ['Fire', 'Earth', 'Air', 'Water', 'Fire', 'Earth', 'Air', 'Water', 'Fire', 'Earth', 'Air', 'Water'];
  var ASPECT_FULL = { 0: 'conjunction', 60: 'sextile', 90: 'square', 120: 'trine', 180: 'opposition' };
  var ORDINAL_WORD = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth'];
  var SMALL_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
  var TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty'];
  var PHASE_WORDS = { 'Waxing Crescent': 'a waxing crescent', 'First Quarter': 'a first-quarter moon', 'Waxing Gibbous': 'a waxing gibbous moon', 'Waning Gibbous': 'a waning gibbous moon', 'Last Quarter': 'a last-quarter moon', 'Waning Crescent': 'a waning crescent' };
  var LIT_STEPS = [[0.1, 'a tenth'], [0.2, 'a fifth'], [0.25, 'a quarter'], [0.33, 'a third'], [0.5, 'half'], [0.67, 'two-thirds'], [0.75, 'three-quarters'], [0.9, 'nine-tenths']];

  var numWord = function (n) { return n <= 20 ? SMALL_WORD[n] : n < 60 ? TENS[Math.floor(n / 10)] + (n % 10 ? '-' + SMALL_WORD[n % 10] : '') : String(n); };
  var signOf = function (lon) { return SIGN_NAMES[Math.floor(lon / 30)]; };
  var elementOf = function (lon) { return ELEMENTS[Math.floor(lon / 30)]; };
  var the = function (n) { return n === 'Sun' || n === 'Moon' ? 'the ' + n : n; };
  var cap = function (t) { return t.charAt(0).toUpperCase() + t.slice(1); };
  // "24 degrees of Pisces"; the first degree is said, not counted from nought
  var degreeOf = function (lon) { var d = Math.floor(lon % 30); return (d === 0 ? 'the first degree' : d + ' degrees') + ' of ' + signOf(lon); };
  var houseWord = function (n) { return n ? 'the ' + ORDINAL_WORD[n] + ' house' : ''; };
  var listWords = function (names) { return names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]; };

  // "a waning crescent, a fifth lit"
  function phraseMoon(ph) {
    var f = ph.illuminated;
    if (ph.name === 'New Moon') return 'a new moon, dark';
    if (ph.name === 'Full Moon') return 'a full moon';
    // the fraction follows the name the engine gave, so the words never contradict the phase entry
    var lit = f < 0.03 ? 'barely lit' : f > 0.97 ? 'all but full' : LIT_STEPS.reduce(function (best, s) { return Math.abs(s[0] - f) < Math.abs(best[0] - f) ? s : best; })[1] + ' lit';
    return (PHASE_WORDS[ph.name] || ph.name.toLowerCase()) + ', ' + lit;
  }
  // the closest major aspect between two planets (nodes left out), within eight degrees
  function tightestAspect(chart) {
    var bodies = chart.bodies.filter(function (b) { return !b.node; });
    var best = null;
    for (var i = 0; i < bodies.length; i++) {
      for (var j = i + 1; j < bodies.length; j++) {
        var sep = Math.abs(bodies[i].lon - bodies[j].lon);
        if (sep > 180) sep = 360 - sep;
        [0, 60, 90, 120, 180].forEach(function (t) {
          var orb = Math.abs(sep - t);
          if (orb < 8 && (!best || orb < best.orb)) best = { a: bodies[i], b: bodies[j], type: ASPECT_FULL[t], orb: orb };
        });
      }
    }
    return best;
  }
  // "the Sun within five arcminutes of Pluto" / "Jupiter in trine to Uranus, twenty-four arcminutes from exact"
  // the orb alone: "five arcminutes" / "two degrees"; and the pair as a subject: "your Sun and Pluto"
  function orbText(asp) {
    var mins = Math.max(1, Math.round(asp.orb * 60)), deg = Math.round(asp.orb);
    return mins < 60 ? numWord(mins) + ' arcminute' + (mins === 1 ? '' : 's') : numWord(deg) + ' degree' + (deg === 1 ? '' : 's');
  }
  var pairText = function (asp) { return 'your ' + asp.a.name + ' and ' + asp.b.name; };
  function phraseAspect(asp) {
    var orb = orbText(asp);
    if (asp.type === 'conjunction') return the(asp.a.name) + ' within ' + orb + ' of ' + the(asp.b.name);
    var rel = asp.type === 'opposition' ? 'in opposition to' : asp.type === 'square' ? 'in square to' : 'in ' + asp.type + ' to';
    return the(asp.a.name) + ' ' + rel + ' ' + the(asp.b.name) + ', ' + orb + ' from exact';
  }

  /* Everything the merge fields can say about a chart. `library` supplies the
     rulers (which planet rules each rising sign) and the principle lines. */
  function factsFor(chart, library) {
    var lib = library || {};
    // without a time and place there are no houses: the house fields stay empty and the ruler's house is unknown
    var houseOf = function (lon) { return chart.cusps ? ENGINE.houseOf(chart.cusps, lon) : null; };
    var sun = chart.bodies[0], moon = chart.bodies[1];
    var rising = chart.solarHouses ? null : signOf(chart.asc);   // no birth time: no rising sign, and so no chart ruler
    var rulerName = rising ? (lib.rulers || {})[rising] : null;
    var ruler = null;
    chart.bodies.forEach(function (b) { if (b.name === rulerName) ruler = b; });
    var retro = chart.bodies.filter(function (b) { return b.retro && !b.node; }).map(function (b) { return b.name; });
    var asp = tightestAspect(chart);
    var principle = lib.principle || {};
    var planets = chart.bodies.filter(function (b) { return !b.node; });
    // the seventh and fifth houses as subjects (the emails): the sign on the cusp and who lives there
    var houseSubject = function (n) {
      if (!chart.cusps) return { sign: '', planets: [] };
      return { sign: signOf(chart.cusps[n - 1]), planets: planets.filter(function (b) { return ENGINE.houseOf(chart.cusps, b.lon) === n; }).map(function (b) { return b.name; }) };
    };
    var h7 = houseSubject(7), h5 = houseSubject(5);
    var balance = balanceOf(planets);
    return {
      sun: sun, moon: moon, rising: rising, ruler: ruler, asp: asp, retro: retro, house7: h7, house5: h5, balance: balance,
      sunHouse: houseOf(sun.lon), moonHouse: houseOf(moon.lon), rulerHouse: ruler ? houseOf(ruler.lon) : null,
      blendKey: elementOf(sun.lon) + '-' + elementOf(moon.lon),
      fields: {
        sun_degree: degreeOf(sun.lon), moon_degree: degreeOf(moon.lon), rising_degree: rising ? degreeOf(chart.asc) : '',
        sun_house: houseWord(houseOf(sun.lon)), moon_house: houseWord(houseOf(moon.lon)),
        sun_sign: signOf(sun.lon), moon_sign: signOf(moon.lon), rising_sign: rising || '',
        moon_phase: phraseMoon(chart.lunarPhase),
        orb: asp ? orbText(asp) : '', aspect_pair: asp ? pairText(asp) : '', Aspect_pair: asp ? cap(pairText(asp)) : '',
        tightest: asp ? phraseAspect(asp) : 'no two planets within eight degrees of a major aspect',
        Tightest: asp ? cap(phraseAspect(asp)) : 'No two planets within eight degrees of a major aspect',
        ruler: ruler ? the(ruler.name) + ', your chart ruler, in ' + signOf(ruler.lon) : 'no single chart ruler',
        Ruler: ruler ? cap(the(ruler.name)) + ', your chart ruler, in ' + signOf(ruler.lon) : 'No single chart ruler',
        ruler_house: ruler ? houseWord(houseOf(ruler.lon)) : '',
        retrogrades: retro.length === 0 ? 'no planet retrograde' : retro.length === 1 ? 'one planet retrograde, ' + retro[0] : numWord(retro.length) + ' planets retrograde: ' + listWords(retro),
        aspect_a: asp ? the(asp.a.name) : '', aspect_b: asp ? the(asp.b.name) : '',
        Aspect_a: asp ? cap(the(asp.a.name)) : '', Aspect_b: asp ? cap(the(asp.b.name)) : '',
        aspect_a_principle: asp ? principle[asp.a.name] || '' : '', aspect_b_principle: asp ? principle[asp.b.name] || '' : '',
        house_7_sign: h7.sign, house_5_sign: h5.sign,
        house_7_planets: listWords(h7.planets.map(the)), house_5_planets: listWords(h5.planets.map(the)),
        House_7_planets: cap(listWords(h7.planets.map(the))), House_5_planets: cap(listWords(h5.planets.map(the))),
        dominant_element: balance.element.dominant ? balance.element.dominant.toLowerCase() : '', lacking_element: balance.element.lacking ? balance.element.lacking.toLowerCase() : '',
        dominant_modality: balance.modality.dominant ? balance.modality.dominant.toLowerCase() : '', lacking_modality: balance.modality.lacking ? balance.modality.lacking.toLowerCase() : '',
        element_counts: balance.element.counts_text, modality_counts: balance.modality.counts_text
      }
    };
  }
  /* the balance of the ten planets by element and by modality. Dominant = the most
     planets, a tie going to the group holding the Sun, then the Moon; lacking = none.
     'even' when nothing leads: the top count under four (elements) or five (modalities). */
  var MODALITY_NAMES = ['Cardinal', 'Fixed', 'Mutable'];
  function balanceOf(planets) {
    var tally = function (names, of, threshold, noun) {
      var counts = {}; names.forEach(function (n) { counts[n] = []; });
      planets.forEach(function (b) { counts[of(b.lon)].push(b); });
      var order = names.slice().sort(function (a, b) {
        var d = counts[b].length - counts[a].length; if (d) return d;
        var holds = function (g, key) { return counts[g].some(function (x) { return x.key === key; }) ? 1 : 0; };
        return (holds(b, 'sun') - holds(a, 'sun')) || (holds(b, 'moon') - holds(a, 'moon')) || names.indexOf(a) - names.indexOf(b);
      });
      var top = order[0], lacking = names.filter(function (n) { return counts[n].length === 0; })[0] || null;
      var parts = order.map(function (n) { var c = counts[n].length; return (c === 0 ? 'none' : numWord(c)) + (n === top ? ' planet' + (c === 1 ? '' : 's') : '') + ' in ' + noun(n); });
      var text = parts.length > 1 ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1] : parts[0];
      return { counts: Object.keys(counts).reduce(function (o, k) { o[k] = counts[k].map(function (b) { return b.name; }); return o; }, {}),
        dominant: counts[top].length >= threshold ? top : null, lacking: lacking, counts_text: text, top: top, top_count: counts[top].length };
    };
    var el = tally(ELEMENTS.slice(0, 4), elementOf, 4, function (n) { return n.toLowerCase(); });
    var mo = tally(MODALITY_NAMES, function (lon) { return MODALITY_NAMES[Math.floor(lon / 30) % 3]; }, 5, function (n) { return n.toLowerCase() + ' signs'; });
    return { element: el, modality: mo, even: !el.dominant && !mo.dominant && !el.lacking && !mo.lacking };
  }
  // the fields that mean "the module's own body": its degree, house and sign
  function localFields(chart, lon) {
    var d = degreeOf(lon);
    var h = houseWord(chart.cusps ? ENGINE.houseOf(chart.cusps, lon) : null);
    return { degree: d, degree_number: d.replace(/ of .*$/, ''), house_ordinal: h, House_ordinal: cap(h), sign: signOf(lon) };
  }
  // {field} → value; the module's own body first, then the chart; an unknown field is marked
  function fill(text, facts, local) {
    var own = local || {};
    return text.replace(/\{([A-Za-z0-9_]+)\}/g, function (m, k) { return k in own ? own[k] : k in facts.fields ? facts.fields[k] : '[' + k + '?]'; });
  }

  /* ================= two charts ==============================================
     The synastry rule lives here, beside the single-chart facts, for one
     reason: the compatibility page prints a count and the compatibility email
     describes that same count in words. If each computed its own the two would
     eventually disagree, and the customer would be the one to notice.

     WHAT COUNTS, AND WHY IT IS THIS AND NOT MORE
     The chart page's aspect table (orrery-analysis.js ASPECTS) is deliberately
     generous: reading one chart, a wide sextile is still worth a sentence.
     Across two charts it is not, and the reason is arithmetic. Every point on
     one side is tested against every point on the other, so a generous table
     over a full point list ran 169 tests and returned very nearly the same
     number for everybody: measured over 4000 random pairs, the middle three
     fifths of all humanity scored between 46 and 55 links. A number that
     cannot vary cannot mean anything, and printing it would have been a
     decoration pretending to be a measurement.

     So: five major aspects at the orbs synastry is normally judged at, over
     nine points a side.
       - the quincunx and semisextile go: a three degree hit between two charts
         is not something either person feels. (pair.aspect.quincunx is
         therefore unreachable copy — left in the library, never rendered.)
       - Uranus, Neptune, Pluto and the Node go: between two people of similar
         age those contacts are close to guaranteed, so they describe a
         generation rather than a couple and flatter every pair equally.
       - Saturn stays. Its contacts are the ones that actually bind, and a
         compatibility reading that cannot mention Saturn is worse than a
         slightly flatter number.
     A typical pair scores 24 where it used to score 66. */
  var SYN_ASPECTS = [
    { name: 'conjunction', angle: 0, orb: 8, cls: 'hard', glyph: '☌' },
    { name: 'sextile', angle: 60, orb: 5, cls: 'soft', glyph: '⚹' },
    { name: 'square', angle: 90, orb: 7, cls: 'hard', glyph: '□' },
    { name: 'trine', angle: 120, orb: 7, cls: 'soft', glyph: '△' },
    { name: 'opposition', angle: 180, orb: 8, cls: 'hard', glyph: '☍' }
  ];
  var SYN_BODIES = { sun: 1, moon: 1, mercury: 1, venus: 1, mars: 1, jupiter: 1, saturn: 1 };
  var synSep = function (a, b) { var d = Math.abs(((a - b) % 360 + 360) % 360); return d > 180 ? 360 - d : d; };

  /* Every point one chart offers the other. The angles join in only when the
     birth time gives them: with no time there is no horizon to aspect. */
  function synPoints(chart) {
    var out = chart.bodies.filter(function (b) { return SYN_BODIES[b.key]; }).map(function (b) {
      return { key: b.key, name: b.name, glyph: b.glyph, lon: b.lon };
    });
    var timed = chart.anglesKnown === undefined ? !chart.solarHouses : chart.anglesKnown;
    if (timed) {
      out.push({ key: 'asc', name: 'Ascendant', glyph: 'ASC', lon: chart.asc });
      out.push({ key: 'mc', name: 'Midheaven', glyph: 'MC', lon: chart.mc });
    }
    return out;
  }
  // every link between the two, tightest first
  function synLinks(chartA, chartB) {
    var pa = synPoints(chartA), pb = synPoints(chartB), out = [];
    pa.forEach(function (x) {
      pb.forEach(function (y) {
        var s = synSep(x.lon, y.lon);
        SYN_ASPECTS.forEach(function (t) {
          var orb = Math.abs(s - t.angle);
          if (orb <= t.orb) out.push({ a: x, b: y, type: t.name, cls: t.cls, glyph: t.glyph, orb: orb });
        });
      });
    });
    out.sort(function (m, n) { return m.orb - n.orb; });
    return out;
  }

  /* THE BANDS, set from the measured distribution of the counter above rather
     than chosen: 4000 random pairs per row, cut at the 20th and 80th
     percentile, so "a lot" is genuinely the top fifth and "few" the bottom.

       both charts timed   9 points a side   p20 21   median 24   p80 28
       one chart untimed   9 and 7           p20 16   median 19   p80 22
       both untimed        7 points a side   p20 12   median 15   p80 17

     Three rows because dropping a birth time removes two points and with them
     several links, and no pair should read as distant for the sole reason that
     nobody wrote the hour down.

     Three bands, not four. A fourth — one chart reaching the other far more
     than the reverse — was written and then dropped: over the same 4000 pairs
     the asymmetry never appears (90th percentile 0.067 of the count, whether
     measured by distinct points involved or by links per point), because a
     symmetric point set makes it very nearly impossible. It would have fired
     on rounding noise and read as a claim about who cares more.
     pair.shape.one-sided is therefore unreachable copy, like the quincunx. */
  var SYN_BANDS = { 18: [21, 28], 16: [16, 22], 14: [12, 17] };
  function synBand(nPoints, n) {
    var t = SYN_BANDS[nPoints] || SYN_BANDS[18];
    return n <= t[0] ? 'quiet' : (n >= t[1] ? 'close' : 'spread');
  }

  /* ================= the scores =============================================
     Six numbers, and every one of them is a percentile.

     WHY A PERCENTILE AND NOT A SCORE OUT OF A HUNDRED
     A weighted sum of aspect strengths is an arbitrary quantity. Printing it
     as a percentage would mean dividing by a maximum nobody will ever reach,
     and the answer would cluster: measured over the reference population,
     three quarters of all pairs land inside a span eleven points wide. A
     number that cannot vary cannot mean anything — the same reason the link
     count was cut back to seven bodies (see "two charts" above).

     So the raw quantity is only ever an intermediate. Each dimension is
     scored, the same scoring is run over a large population of random pairs
     (tests/score-distribution.mjs), and what the page prints is where this
     pair falls in that spread. Fifty means the middle of the population. Ten
     means nine pairs in ten have more of that kind of contact. The tables are
     generated into assets/orrery-scale.js and the page cannot compute a
     percentile without them.

     THREE TABLES, BY WHAT IS KNOWN
     Dropping a birth time removes the Ascendant and the Midheaven, and with
     them every contact to an angle and the seventh-house overlay. So there are
     three populations — both charts timed, one timed, neither — and a pair is
     ranked against the population that knows what it knows. Nobody scores low
     because nobody wrote the hour down.

     WHAT EACH CONTACT IS WORTH
     strength = aspect value x tightness x the two points' weights for that
     dimension. Tightness runs from 1 at exact to 0.35 at the edge of orb, so
     a contact two degrees off counts for more than one seven degrees off and
     neither is counted as nothing. The aspect values and every point weight
     are in the tables below, and the page prints them under "how this is
     worked out" — a weight nobody can read is a weight nobody can argue with.

     THE HEADLINE IS CONTACT, NOT HAPPINESS
     The overall number is the weighted sum of the five dimension percentiles,
     ranked in its turn. Friction counts toward it POSITIVELY and is not
     subtracted: two charts in hard aspect are touching hard, and that is what
     this number measures. It is not a forecast and it is not a verdict, and
     the page says so in the line directly under it. */

  // what an exact aspect of each kind is worth, before the points are weighed
  var ASPECT_VALUE = { conjunction: 1, trine: 0.85, opposition: 0.8, square: 0.75, sextile: 0.55 };
  var TIGHT_FLOOR = 0.35;          // a contact at the very edge of orb still counts for this much
  var OVERLAY = 0.45;              // one chart's planet standing in the other's seventh house
  var HARD = { conjunction: 1, square: 1, opposition: 1 };
  var ORB_OF = {};
  SYN_ASPECTS.forEach(function (a) { ORB_OF[a.name] = a.orb; });

  /* The five dimensions. `takes` decides whether a contact belongs to this
     dimension at all; `w` is how much each point counts in it; `soft` and
     `hard` scale the whole contact by the kind of angle, because a square
     between two charts is real contact in every dimension and easy contact in
     none. A point missing from `w` is a point the dimension does not count. */
  var SCORE_DIMS = [
    { key: 'attraction', weight: 22,
      takes: function (l) { return l.a.key === 'venus' || l.a.key === 'mars' || l.b.key === 'venus' || l.b.key === 'mars'; },
      w: { venus: 1, mars: 1, sun: 0.75, moon: 0.75, asc: 0.7, jupiter: 0.45, mercury: 0.35, mc: 0.35, saturn: 0.3 },
      hard: 0.85 },
    { key: 'communication', weight: 22,
      takes: function (l) { return l.a.key === 'mercury' || l.a.key === 'moon' || l.b.key === 'mercury' || l.b.key === 'moon'; },
      w: { mercury: 1, moon: 1, sun: 0.7, jupiter: 0.6, venus: 0.6, asc: 0.6, mars: 0.5, saturn: 0.45, mc: 0.4 },
      hard: 0.5 },
    { key: 'friction', weight: 18,
      // every square and opposition; a conjunction only where Saturn or Mars is one end of it
      takes: function (l) { return l.type === 'square' || l.type === 'opposition' ||
        (l.type === 'conjunction' && (l.a.key === 'mars' || l.b.key === 'mars' || l.a.key === 'saturn' || l.b.key === 'saturn')); },
      w: { mars: 1, saturn: 1, sun: 0.8, moon: 0.8, venus: 0.7, asc: 0.7, mercury: 0.6, mc: 0.5, jupiter: 0.4 },
      hard: 1 },
    { key: 'staying', weight: 20,
      // Saturn or an angle at either end, or the two lights to each other
      takes: function (l) {
        var k = { saturn: 1, asc: 1, mc: 1 };
        if (k[l.a.key] || k[l.b.key]) return true;
        var light = { sun: 1, moon: 1 };
        return !!(light[l.a.key] && light[l.b.key]);
      },
      w: { saturn: 1, sun: 0.9, moon: 0.9, asc: 0.9, mc: 0.7, venus: 0.6, mars: 0.5, jupiter: 0.5, mercury: 0.35 },
      hard: 0.8 }
    // everyday fit takes no contacts at all: it compares the two charts' element
    // and modality weightings, and is the one dimension a missing birth time
    // costs nothing. Its weight is below, in SCORE_FIT.
  ];
  var SCORE_FIT = { key: 'fit', weight: 18 };
  var SCORE_ORDER = ['attraction', 'communication', 'friction', 'staying', 'fit'];

  /* Element and modality affinity: the conventional pairings, written down so
     they can be argued with. These are the only numbers on the page that are
     not measured — they are a convention, and the panel says as much. */
  var ELEM_FIT = {
    'Fire-Fire': 1, 'Earth-Earth': 1, 'Air-Air': 1, 'Water-Water': 1,
    'Fire-Air': 0.85, 'Earth-Water': 0.85, 'Air-Water': 0.45,
    'Fire-Earth': 0.35, 'Earth-Air': 0.35, 'Fire-Water': 0.25
  };
  var MODE_FIT = {
    'Cardinal-Mutable': 0.8, 'Fixed-Mutable': 0.6, 'Mutable-Mutable': 0.55,
    'Cardinal-Cardinal': 0.5, 'Cardinal-Fixed': 0.45, 'Fixed-Fixed': 0.4
  };
  var fitOf = function (table, x, y) { var v = table[x + '-' + y]; return v === undefined ? table[y + '-' + x] : v; };
  // how much each body counts in the everyday-fit weighting: the same seven the
  // links use, for the same reason — the outer planets describe a generation
  var FIT_WEIGHT = { sun: 3, moon: 3, mercury: 2, venus: 2, mars: 2, jupiter: 1, saturn: 1 };
  var MODE_NAMES = ['Cardinal', 'Fixed', 'Mutable'];
  var modalityOf = function (lon) { return MODE_NAMES[Math.floor(lon / 30) % 3]; };

  // every weighted body of one chart against every one of the other, averaged
  function fitRaw(chartA, chartB) {
    var pick = function (c) {
      return c.bodies.filter(function (b) { return FIT_WEIGHT[b.key]; }).map(function (b) {
        return { key: b.key, name: b.name, w: FIT_WEIGHT[b.key], element: elementOf(b.lon), modality: modalityOf(b.lon) };
      });
    };
    var pa = pick(chartA), pb = pick(chartB);
    var el = 0, mo = 0, tot = 0;
    pa.forEach(function (x) {
      pb.forEach(function (y) {
        var w = x.w * y.w;
        el += w * fitOf(ELEM_FIT, x.element, y.element);
        mo += w * fitOf(MODE_FIT, x.modality, y.modality);
        tot += w;
      });
    });
    if (!tot) return { raw: 0, element: 0, modality: 0, a: pa, b: pb };
    var e = el / tot, m = mo / tot;
    return { raw: 0.6 * e + 0.4 * m, element: e, modality: m, a: pa, b: pb };
  }

  // which of the three populations this pair belongs to
  function synTiming(chartA, chartB) {
    var timed = function (c) { return c.anglesKnown === undefined ? !c.solarHouses : c.anglesKnown; };
    var n = (timed(chartA) ? 1 : 0) + (timed(chartB) ? 1 : 0);
    return n === 2 ? 'both' : n === 1 ? 'one' : 'none';
  }
  var hasCusps = function (c) { return !!(c.cusps && !c.solarHouses); };

  /* One pair, scored. Returns the raw quantity per dimension and, beside it,
     every contact that went into it with what it contributed — the page shows
     that list when a row is opened, because a number whose parts are hidden is
     a number nobody can check. */
  function synRaw(chartA, chartB) {
    var links = synLinks(chartA, chartB);
    var dims = {};
    SCORE_DIMS.forEach(function (d) {
      var raw = 0, parts = [];
      links.forEach(function (l) {
        if (!d.takes(l)) return;
        var wa = d.w[l.a.key], wb = d.w[l.b.key];
        if (!wa || !wb) return;
        var orbMax = ORB_OF[l.type];
        var tight = 1 - (1 - TIGHT_FLOOR) * (l.orb / orbMax);
        var v = ASPECT_VALUE[l.type] * tight * wa * wb * (HARD[l.type] ? d.hard : 1);
        raw += v;
        parts.push({ kind: 'link', a: l.a, b: l.b, type: l.type, orb: l.orb, value: v });
      });
      dims[d.key] = { raw: raw, parts: parts };
    });
    /* The seventh house, both ways round. One chart's planet standing in the
       other's house of partnership is the oldest claim in synastry and it needs
       a birth time to make; each direction is gated on its own chart, so a pair
       with one time still gets the half it has earned. */
    var overlay = function (host, guest, mine) {
      if (!hasCusps(host)) return;
      synPoints(guest).forEach(function (p) {
        if (p.key === 'asc' || p.key === 'mc') return;
        if (ENGINE.houseOf(host.cusps, p.lon) !== 7) return;
        var w = SCORE_DIMS[3].w[p.key] || 0.4;
        var v = OVERLAY * w;
        dims.staying.raw += v;
        dims.staying.parts.push({ kind: 'house7', point: p, mine: mine, value: v });
      });
    };
    overlay(chartA, chartB, true);
    overlay(chartB, chartA, false);
    SCORE_DIMS.forEach(function (d) { dims[d.key].parts.sort(function (m, n) { return n.value - m.value; }); });
    var fit = fitRaw(chartA, chartB);
    dims.fit = { raw: fit.raw, parts: [], fit: fit };
    return { dims: dims, links: links, timing: synTiming(chartA, chartB) };
  }

  /* Where a raw value falls in a measured spread. The table is 101 raw values,
     one for each whole percentile, generated by tests/score-distribution.mjs;
     a value between two of them is interpolated, so the answer moves smoothly
     and two pairs a whisker apart do not print the same number. */
  function percentileIn(table, raw) {
    if (!table || !table.length) return null;
    var n = table.length - 1;
    if (raw <= table[0]) return 0;
    if (raw >= table[n]) return 100;
    var lo = 0, hi = n;
    while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (table[mid] <= raw) lo = mid; else hi = mid; }
    var span = table[hi] - table[lo];
    var frac = span > 0 ? (raw - table[lo]) / span : 0;
    return (lo + frac) * (100 / n);
  }

  /* The six numbers. `scale` is the generated table set (window.OrreryScale);
     without it there are no percentiles and the function says so rather than
     inventing a denominator. */
  function synScores(chartA, chartB, scale) {
    var sc = scale || window.OrreryScale;
    var r = synRaw(chartA, chartB);
    var tables = sc && sc.tables && sc.tables[r.timing];
    var dims = {}, weighted = 0, totalWeight = 0;
    var all = SCORE_DIMS.concat([SCORE_FIT]);
    all.forEach(function (d) {
      var raw = r.dims[d.key].raw;
      var pct = tables ? percentileIn(tables[d.key], raw) : null;
      dims[d.key] = { key: d.key, raw: raw, pct: pct, score: pct == null ? null : Math.round(pct), weight: d.weight, parts: r.dims[d.key].parts, fit: r.dims[d.key].fit };
      if (pct != null) { weighted += d.weight * pct; totalWeight += d.weight; }
    });
    var overallRaw = totalWeight ? weighted / totalWeight : null;
    var overallPct = (tables && overallRaw != null) ? percentileIn(tables.overall, overallRaw) : null;
    return {
      timing: r.timing, links: r.links, count: r.links.length,
      order: SCORE_ORDER, dims: dims,
      overall: { raw: overallRaw, pct: overallPct, score: overallPct == null ? null : Math.round(overallPct) },
      scaled: !!tables, population: sc ? sc.population : null,
      weights: all.reduce(function (o, d) { o[d.key] = d.weight; return o; }, {})
    };
  }

  // the published constants, so the page can print the weights it used rather
  // than a sentence claiming there were some
  var SCORE_SPEC = {
    aspect: ASPECT_VALUE, tightFloor: TIGHT_FLOOR, overlay: OVERLAY,
    dims: SCORE_DIMS, fit: SCORE_FIT, order: SCORE_ORDER,
    element: ELEM_FIT, modality: MODE_FIT, fitWeight: FIT_WEIGHT, orbs: ORB_OF
  };

  /* ================= the tangible characteristics ===========================
     Four or five short lines naming what the two of you are actually like
     together, in words a non-astrologer reads without stopping.

     Each line is selected by a real placement and shows that placement beside
     it. The selector is deliberately coarse — the modality of Mars, the element
     of Mercury, Venus and the Moon — because a coarse selector over a real
     placement is honest, and a fine one over the same placement would only be
     pretending to more resolution than two signs can carry.

     The line itself is library copy (pair.trait.*), keyed on the two sides'
     categories sorted into a fixed order, so there is one entry per pairing
     rather than one per ordering. The copy therefore says "one of you ... the
     other", and the trace line under it says which is which, in the same order
     as the key. That order is what `first` reports.

     The glosses below are the one piece of prose in this file. They are labels
     for a category, not a reading — the same kind of thing as PHASE_WORDS
     above — and they exist so the trace line explains itself rather than
     naming a sign and leaving the reader to look it up. */
  var TRAIT_FAMILIES = [
    { key: 'pace', body: 'mars', quality: 'modality', cats: ['cardinal', 'fixed', 'mutable'],
      cat: { Cardinal: 'cardinal', Fixed: 'fixed', Mutable: 'mutable' },
      gloss: { cardinal: 'a sign that moves first', fixed: 'a sign that digs in', mutable: 'a sign that changes direction' } },
    { key: 'talk', body: 'mercury', quality: 'element', cats: ['out', 'in'],
      cat: { Fire: 'out', Air: 'out', Earth: 'in', Water: 'in' },
      gloss: { out: 'a sign that thinks out loud', 'in': 'a sign that thinks it through first' } },
    { key: 'warmth', body: 'venus', quality: 'element', cats: ['say', 'do'],
      cat: { Fire: 'say', Air: 'say', Earth: 'do', Water: 'do' },
      gloss: { say: 'a sign that says it', 'do': 'a sign that shows it by doing' } },
    { key: 'space', body: 'moon', quality: 'element', cats: ['out', 'in'],
      cat: { Fire: 'out', Air: 'out', Earth: 'in', Water: 'in' },
      gloss: { out: 'a sign that takes a mood outward', 'in': 'a sign that takes a mood inward' } }
  ];
  var bodyOf = function (chart, key) { for (var i = 0; i < chart.bodies.length; i++) if (chart.bodies[i].key === key) return chart.bodies[i]; return null; };

  /* Which side's Saturn is leaning on the other's personal planets. Saturn is
     the one body whose direction matters: "you are the steadying one" and "they
     are" are different sentences about different people, so this is measured
     per side rather than pooled. */
  var SATURN_TARGETS = { sun: 1, moon: 1, mercury: 0.7, venus: 0.9, mars: 0.8, asc: 0.9 };
  function saturnLean(chartA, chartB) {
    var score = function (from, to) {
      var sat = bodyOf(from, 'saturn');
      if (!sat) return { v: 0, n: 0 };
      var v = 0, n = 0;
      synPoints(to).forEach(function (p) {
        var w = SATURN_TARGETS[p.key];
        if (!w) return;
        var s = synSep(sat.lon, p.lon);
        SYN_ASPECTS.forEach(function (t) {
          var orb = Math.abs(s - t.angle);
          if (orb > t.orb) return;
          v += w * (1 - (1 - TIGHT_FLOOR) * (orb / t.orb)); n++;
        });
      });
      return { v: v, n: n };
    };
    return { mine: score(chartA, chartB), theirs: score(chartB, chartA) };
  }

  /* The lines, in reading order. Returns the library key, which side the key's
     first category belongs to, and the placements the choice rests on. */
  function synTraits(chartA, chartB) {
    var out = [];
    TRAIT_FAMILIES.forEach(function (f) {
      var ba = bodyOf(chartA, f.body), bb = bodyOf(chartB, f.body);
      if (!ba || !bb) return;
      var qa = f.quality === 'modality' ? modalityOf(ba.lon) : elementOf(ba.lon);
      var qb = f.quality === 'modality' ? modalityOf(bb.lon) : elementOf(bb.lon);
      var ca = f.cat[qa], cb = f.cat[qb];
      // the key is the pairing, not the ordering; `first` says who the first half is
      var flip = f.cats.indexOf(cb) < f.cats.indexOf(ca);
      var k1 = flip ? cb : ca, k2 = flip ? ca : cb;
      out.push({
        family: f.key, key: 'pair.trait.' + f.key + '.' + k1 + '-' + k2,
        first: flip ? 'b' : 'a', body: ba.name,
        source: [
          { side: flip ? 'b' : 'a', body: (flip ? bb : ba).name, sign: signOf((flip ? bb : ba).lon), gloss: f.gloss[k1] },
          { side: flip ? 'a' : 'b', body: (flip ? ba : bb).name, sign: signOf((flip ? ba : bb).lon), gloss: f.gloss[k2] }
        ]
      });
    });
    // Saturn, when there is any to speak of
    var lean = saturnLean(chartA, chartB);
    var total = lean.mine.v + lean.theirs.v;
    if (total >= 0.3) {
      var which = lean.mine.v > lean.theirs.v * 1.4 ? 'mine' : lean.theirs.v > lean.mine.v * 1.4 ? 'theirs' : 'both';
      out.push({
        family: 'saturn', key: 'pair.trait.saturn.' + which, first: which === 'theirs' ? 'b' : 'a',
        body: 'Saturn', counts: { a: lean.mine.n, b: lean.theirs.n },
        source: [{ side: 'a', body: 'Saturn', contacts: lean.mine.n }, { side: 'b', body: 'Saturn', contacts: lean.theirs.n }]
      });
    }
    return out;
  }

  /* ---- the pair's merge fields
     Whose planet a clause belongs to is the one thing this must never get
     wrong: principle reads "what you love", principle_their reads "what they
     love", and the same clause attached to the wrong person is not a small
     error, it is about the wrong person. So side A always takes `principle`
     and side B always takes `principle_their`, and there is no path that
     swaps them. */
  var ELEM_ORDER = ['Fire', 'Earth', 'Air', 'Water'];
  // "your" / "Maya’s"  —  the adjective, before a noun
  var possAdj = function (name, mine) { return name ? name + '’s' : (mine ? 'your' : 'their'); };
  // "yours" / "Maya’s" —  standing alone, after "three of"
  var possAlone = function (name, mine) { return name ? name + '’s' : (mine ? 'yours' : 'theirs'); };

  /* The named link. The Ascendant and the Midheaven have no principle clause
     in the library, so a link on an angle is counted but cannot be described;
     the tightest link with a clause at BOTH ends is the one named, never a
     half sentence. Returns null if no link qualifies. */
  function namedLink(links, principle, principleTheir) {
    for (var i = 0; i < links.length; i++) {
      if (principle[links[i].a.name] && principleTheir[links[i].b.name]) return links[i];
    }
    return null;
  }

  /* One side's element and modality weighting, over the same ten planets the
     single-chart reading uses — so a customer's element balance says the same
     thing in their chart email and in their compatibility email. */
  function sideBalance(chart) { return balanceOf(chart.bodies.filter(function (b) { return !b.node; })); }

  /* "four of your planets in earth, three of theirs in air" — written in the
     order the library key names the groups, so the counts follow the sentence
     that introduces them ("one of you leans to earth and the other to air").
     When both sides share a group the second count needs no group named. */
  function pairCounts(groupA, groupB, a, b, tallyA, tallyB, noun) {
    var first = ELEM_ORDER.indexOf(groupA) >= 0 ? ELEM_ORDER : MODALITY_NAMES;
    var flip = groupA !== groupB && first.indexOf(groupB) < first.indexOf(groupA);
    var g1 = flip ? groupB : groupA, g2 = flip ? groupA : groupB;
    var s1 = flip ? b : a, s2 = flip ? a : b;
    var t1 = flip ? tallyB : tallyA, t2 = flip ? tallyA : tallyB;
    var n1 = t1.counts[g1].length, n2 = t2.counts[g2].length;
    var one = numWord(n1) + ' of ' + possAdj(s1.name, s1.mine) + ' planets in ' + noun(g1);
    var two = numWord(n2) + ' of ' + possAlone(s2.name, s2.mine) + (g1 === g2 ? '' : ' in ' + noun(g2));
    return { key: g1 + '-' + g2, text: one + (g1 === g2 ? ' and ' : ', ') + two };
  }

  /* Everything the pair entries can say. `a` and `b` are { name, chart }; a is
     always the reader and b always the other person. `library` supplies the
     principle clauses, as it does for the single chart. */
  function pairFactsFor(a, b, library) {
    var lib = library || {};
    var principle = lib.principle || {}, principleTheir = lib.principle_their || {};
    var sideA = { name: (a.name || '').trim(), mine: true }, sideB = { name: (b.name || '').trim(), mine: false };
    var links = synLinks(a.chart, b.chart);
    var nPoints = synPoints(a.chart).length + synPoints(b.chart).length;
    var band = synBand(nPoints, links.length);
    var bal = sideBalance(a.chart), balB = sideBalance(b.chart);
    var el = pairCounts(bal.element.top, balB.element.top, sideA, sideB, bal.element, balB.element, function (n) { return n.toLowerCase(); });
    var mo = pairCounts(bal.modality.top, balB.modality.top, sideA, sideB, bal.modality, balB.modality, function (n) { return n.toLowerCase(); });
    var link = namedLink(links, principle, principleTheir);
    var aName = link ? possAdj(sideA.name, true) + ' ' + link.a.name : '';
    var bName = link ? possAdj(sideB.name, false) + ' ' + link.b.name : '';
    var untimed = !a.chart.cusps || !b.chart.cusps || a.chart.solarHouses || b.chart.solarHouses;
    return {
      links: links, count: links.length, band: band, link: link, untimed: !!untimed,
      // which library entries this pair reaches: pair.shape.<band>, pair.aspect.<type>, pair.element.<key>, pair.modality.<key>
      keys: { shape: band, aspect: link ? link.type : null, element: el.key, modality: mo.key },
      fields: {
        link_count: cap(numWord(links.length)) + ' link' + (links.length === 1 ? '' : 's'),
        Pair_a: cap(aName), Pair_b: bName,
        A_planet: cap(aName), B_planet: cap(bName),
        pair_a_principle: link ? principle[link.a.name] || '' : '',
        pair_b_principle: link ? principleTheir[link.b.name] || '' : '',
        orb: link ? orbText(link) : '',
        pair_element_counts: el.text, pair_modality_counts: mo.text
      }
    };
  }

  window.OrreryFacts = { factsFor: factsFor, pairFactsFor: pairFactsFor, synPoints: synPoints, synLinks: synLinks, synBand: synBand, SYN_ASPECTS: SYN_ASPECTS,
    synRaw: synRaw, synScores: synScores, synTraits: synTraits, TRAIT_FAMILIES: TRAIT_FAMILIES, synTiming: synTiming, percentileIn: percentileIn, fitRaw: fitRaw, modalityOf: modalityOf, SCORE_SPEC: SCORE_SPEC, balanceOf: balanceOf, localFields: localFields, fill: fill, signOf: signOf, elementOf: elementOf, degreeOf: degreeOf, houseWord: houseWord, phraseMoon: phraseMoon, phraseAspect: phraseAspect, tightestAspect: tightestAspect, VERSION: 2 };
})();
